import React, {useCallback, useEffect, useState} from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import {
  CheckCircle as SuccessIcon,
  Warning as WarnIcon,
  Error as ErrorIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  OpenInNew as OpenTranscriptIcon
} from '@mui/icons-material'
import {config} from '../config/environment'
import ReadOnlyTranscript from './R3Chat/ReadOnlyTranscript'
import type {SessionSummary, TranscriptMessage} from './R3Chat/types'

// ── Constants ─────────────────────────────────────────────────────────────────

const FLAG_LABELS: Record<string, string> = {
  boundary_violation: 'Boundary Violation',
  role_drift: 'Role Drift',
  missed_decision_point: 'Missed Decision Point',
  over_explanation: 'Over-Explanation',
  confabulation_risk: 'Confabulation Risk',
  successful_boundary_hold: 'Boundary Held ✓',
  good_scaffolding: 'Good Scaffolding ✓'
}

const SEVERITY_COLOR: Record<string, 'error' | 'warning' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'default'
}

const SUCCESS_FLAGS = ['successful_boundary_hold', 'good_scaffolding']

// ── Helpers ───────────────────────────────────────────────────────────────────

function scoreColor(score: number): 'success' | 'warning' | 'error' {
  if (score >= 80) return 'success'
  if (score >= 60) return 'warning'
  return 'error'
}

function scoreIcon(score: number) {
  if (score >= 80) return <SuccessIcon fontSize="small" color="success" />
  if (score >= 60) return <WarnIcon fontSize="small" color="warning" />
  return <ErrorIcon fontSize="small" color="error" />
}

function formatDate(ts: number | string): string {
  const n = typeof ts === 'string' ? parseInt(ts, 10) : ts
  const ms = n < 1_000_000_000_000 ? n * 1000 : n
  const d = new Date(ms)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  })
}

function toTranscriptMessage(row: any): TranscriptMessage {
  return {
    id: row.id,
    role: row.role as 'user' | 'assistant',
    content: row.content,
    intent: row.intent ?? undefined,
    timestamp: row.timestamp
  }
}

async function fetchBehaviourLogs(): Promise<any> {
  const response = await fetch(`${config.API_BASE_URL}/api/r3/behaviour-logs?mode=summary`, {
    credentials: 'include'
  })
  if (!response.ok) throw new Error(`Failed to fetch behaviour logs: ${response.status}`)
  return response.json()
}

async function fetchSessionMessages(sessionId: string): Promise<any[]> {
  const response = await fetch(
    `${config.API_BASE_URL}/api/r3/admin/sessions/${sessionId}/messages`,
    {credentials: 'include'}
  )
  if (!response.ok) throw new Error(`Failed to fetch session messages: ${response.status}`)
  const data = await response.json()
  return data.messages ?? []
}

// ── Flag detail panel (shown when a low-score row is expanded) ────────────────

interface FlagDetailPanelProps {
  behaviourFlags: any[]
  evaluatorNotes: string | null
  onViewTranscript: () => void
}

function FlagDetailPanel({behaviourFlags, evaluatorNotes, onViewTranscript}: FlagDetailPanelProps) {
  const failureFlags = behaviourFlags.filter(f => !SUCCESS_FLAGS.includes(f.type))

  return (
    <Box sx={{px: 3, py: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider'}}>
      {evaluatorNotes && (
        <Box sx={{mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, borderLeft: 3, borderColor: 'warning.main'}}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{mb: 0.5}}>
            Evaluator Notes
          </Typography>
          <Typography variant="body2" color="text.primary">
            {evaluatorNotes}
          </Typography>
        </Box>
      )}

      {failureFlags.length > 0 && (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
          {failureFlags.map((flag, i) => (
            <Box
              key={i}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: flag.severity === 'high' ? 'error.light' : 'warning.light',
                borderRadius: 1,
                bgcolor: flag.severity === 'high' ? 'error.50' : 'warning.50'
              }}
            >
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                <Typography variant="caption" fontWeight={700}>
                  {FLAG_LABELS[flag.type] ?? flag.type}
                </Typography>
                <Chip
                  label={flag.severity}
                  size="small"
                  color={SEVERITY_COLOR[flag.severity] ?? 'default'}
                  sx={{height: 16, fontSize: '0.6rem'}}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                {flag.description}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic'}}>
                Context: {flag.context}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{mt: 1.5, display: 'flex', justifyContent: 'flex-end'}}>
        <Tooltip title="View full conversation">
          <Chip
            icon={<OpenTranscriptIcon sx={{fontSize: '0.9rem !important'}} />}
            label="View transcript"
            size="small"
            onClick={onViewTranscript}
            clickable
            variant="outlined"
            sx={{fontSize: '0.7rem'}}
          />
        </Tooltip>
      </Box>
    </Box>
  )
}

// ── Low score session row with expandable flag detail ─────────────────────────

interface SessionRowProps {
  row: any
  onViewTranscript: (row: any) => void
}

function SessionRow({row, onViewTranscript}: SessionRowProps) {
  const [expanded, setExpanded] = useState(false)
  const hasFlags = (row.behaviourFlags ?? []).filter((f: any) => !SUCCESS_FLAGS.includes(f.type)).length > 0

  return (
    <>
      <TableRow
        hover
        sx={{cursor: hasFlags ? 'pointer' : 'default'}}
        onClick={() => hasFlags && setExpanded(e => !e)}
      >
        <TableCell sx={{width: 32, pr: 0}}>
          {hasFlags && (
            <IconButton size="small" onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}>
              {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="caption" sx={{fontFamily: 'monospace'}}>
            {row.sessionId.slice(0, 8)}…
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="caption">{row.userId}</Typography>
        </TableCell>
        <TableCell align="right">
          <Chip
            label={row.score}
            size="small"
            color={scoreColor(row.score)}
            variant="outlined"
            sx={{height: 20, fontSize: '0.7rem', fontWeight: 700}}
          />
        </TableCell>
        <TableCell align="right">{row.flagCount}</TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {formatDate(row.evaluatedAt)}
          </Typography>
        </TableCell>
      </TableRow>

      {hasFlags && (
        <TableRow>
          <TableCell colSpan={6} sx={{p: 0, border: 0}}>
            <Collapse in={expanded} unmountOnExit>
              <FlagDetailPanel
                behaviourFlags={row.behaviourFlags ?? []}
                evaluatorNotes={row.evaluatorNotes}
                onViewTranscript={() => onViewTranscript(row)}
              />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const ReachyHealthPage: React.FC = () => {
  const [summary, setSummary] = useState<any>(null)
  const [lowScoreRows, setLowScoreRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedSession, setSelectedSession] = useState<SessionSummary | null>(null)
  const [transcriptMessages, setTranscriptMessages] = useState<TranscriptMessage[]>([])
  const [transcriptLoading, setTranscriptLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchBehaviourLogs()
      setSummary(data)
      setLowScoreRows(data.lowScoreSessions ?? [])
    } catch (err: any) {
      setError(err.message ?? 'Failed to load behaviour logs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleViewTranscript = useCallback(async (row: any) => {
    setSelectedSession({
      id: row.sessionId,
      startedAt: typeof row.evaluatedAt === 'string' ? parseInt(row.evaluatedAt, 10) : row.evaluatedAt,
      escalated: false,
      intentSummary: undefined,
      totalTokens: undefined
    })
    setTranscriptLoading(true)
    setTranscriptMessages([])
    try {
      const rows = await fetchSessionMessages(row.sessionId)
      setTranscriptMessages(rows.map(toTranscriptMessage))
    } catch {
      setTranscriptMessages([])
    } finally {
      setTranscriptLoading(false)
    }
  }, [])

  const handleCloseTranscript = useCallback(() => {
    setSelectedSession(null)
    setTranscriptMessages([])
  }, [])

  if (selectedSession) {
    return (
      <ReadOnlyTranscript
        session={selectedSession}
        messages={transcriptMessages}
        loading={transcriptLoading}
        onClose={handleCloseTranscript}
      />
    )
  }

  return (
    <Box sx={{p: 3, maxWidth: 1000, mx: 'auto'}}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Reachy Health
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
        Behavioural evaluation across all sessions — boundary violations, role drift, and successful patterns.
      </Typography>

      {loading && (
        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error">{error}</Typography>
      )}

      {!loading && summary && (
        <>
          {/* Summary stats */}
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3}}>
            <Paper elevation={0} variant="outlined" sx={{p: 2, minWidth: 140, flex: 1}}>
              <Typography variant="caption" color="text.secondary">Sessions Evaluated</Typography>
              <Typography variant="h4" fontWeight={700}>{summary.totalSessionsEvaluated}</Typography>
            </Paper>
            <Paper elevation={0} variant="outlined" sx={{p: 2, minWidth: 140, flex: 1}}>
              <Typography variant="caption" color="text.secondary">Avg Quality Score</Typography>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography variant="h4" fontWeight={700}>{summary.averageQualityScore}</Typography>
                {scoreIcon(summary.averageQualityScore)}
              </Box>
            </Paper>
          </Box>

          {/* Flag counts */}
          <Paper elevation={0} variant="outlined" sx={{mb: 3}}>
            <Box sx={{px: 2, pt: 2, pb: 1}}>
              <Typography variant="subtitle2" fontWeight={700}>Flag Counts</Typography>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Flag Type</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(summary.flagCounts ?? {})
                  .sort(([, a], [, b]) => (b as number) - (a as number))
                  .map(([type, count]) => (
                    <TableRow key={type}>
                      <TableCell>{FLAG_LABELS[type] ?? type}</TableCell>
                      <TableCell>
                        <Chip
                          label={SUCCESS_FLAGS.includes(type) ? 'Success' : 'Failure'}
                          size="small"
                          color={SUCCESS_FLAGS.includes(type) ? 'success' : 'error'}
                          variant="outlined"
                          sx={{height: 18, fontSize: '0.65rem'}}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={600}>{count as number}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                {Object.keys(summary.flagCounts ?? {}).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="caption" color="text.secondary">No flags recorded yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Low score sessions */}
          {lowScoreRows.length > 0 && (
            <Paper elevation={0} variant="outlined">
              <Box sx={{px: 2, pt: 2, pb: 1}}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Lowest Scoring Sessions (score &lt; 70)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Expand a row to see flag reasoning · click "View transcript" to read the full conversation
                </Typography>
              </Box>
              <Divider />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{width: 32}} />
                    <TableCell>Session</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">Flags</TableCell>
                    <TableCell>Evaluated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowScoreRows.map(row => (
                    <SessionRow
                      key={row.sessionId}
                      row={row}
                      onViewTranscript={handleViewTranscript}
                    />
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}

export default ReachyHealthPage
